import { Fragment, type FC } from "react";
import { useSelector } from "react-redux";
import {
  type ActionLink,
  CarSelfServeModalType,
  CoreCarsComponent,
  useCarsStyles,
  type CarReservationSelfServeLinksProps as CoreCarReservationSelfServeLinksProps,
  useCarReservationSelfServeLinks,
} from "@apac/components/cars";
import { SupportHubSource } from "@b2bportal/core-self-serve";
import { IconNameEnum, useModuleBEM } from "@b2bportal/core-themes";
import { BookingType } from "@b2bportal/core-types";
import { ensureExhaustive } from "@b2bportal/core-utilities";
import { useI18nContext } from "@hopper-b2b/i18n";
import { getDelegatedTo } from "@hopper-b2b/self-serve";
import { ResendEmailModal } from "@tenant/self-serve";
import { Button, ButtonTypeEnum, ContactSupportModal, Icon } from "@tenant/ui";
import defaultStyles from "./CarReservationSelfServeLinks.module.scss";

export interface CarReservationSelfServeLinksProps
  extends CoreCarReservationSelfServeLinksProps {
  onCancel: () => void;
}

export const CarReservationSelfServeLinks: FC<
  CarReservationSelfServeLinksProps
> = (props) => {
  const styles = useCarsStyles(
    CoreCarsComponent.CarReservationSelfServeLinks,
    defaultStyles
  );
  const [block, cn] = useModuleBEM(
    styles,
    CoreCarsComponent.CarReservationSelfServeLinks
  );

  const { t } = useI18nContext();
  const { reservation, onCancel } = props;
  const { actions } = useCarReservationSelfServeLinks(props);

  const isAgentImpersonation = !!useSelector(getDelegatedTo);

  const getSelfServeContent = (action: ActionLink) => {
    switch (action.modalType) {
      case CarSelfServeModalType.Cancel:
        return (
          <Button buttonType={ButtonTypeEnum.Secondary} onClick={onCancel}>
            {action.content}
          </Button>
        );
      case CarSelfServeModalType.ResendEmail:
        return (
          <ResendEmailModal
            key={action.modalType}
            title={action.content}
            reservationId={reservation.reservation.internalGroundBookingId}
            reservationType={BookingType.Ground}
            initialValue={reservation.reservation.driverInfo.emailAddress}
            disabled={isAgentImpersonation}
          />
        );
      case CarSelfServeModalType.ContactSupport:
        return (
          <ContactSupportModal
            enableHtsAssistChoice
            key={action.modalType}
            source={SupportHubSource.manage_booking}
            trigger={
              <>
                <Icon iconName={IconNameEnum.questionCircle} />
                {t("selfServe.contactSupport")}
              </>
            }
          />
        );
      case CarSelfServeModalType.Invoice:
        return action.content;
      default:
        ensureExhaustive(action.modalType);
    }
  };

  return (
    <div className={block}>
      <h3 className={cn("title")}>{t("trips.manageBooking")}</h3>
      <div className={cn("actions-container")}>
        {actions.map((action, idx) => (
          <Fragment key={idx}>{getSelfServeContent(action)}</Fragment>
        ))}
      </div>
    </div>
  );
};
